import React, { useMemo } from "react";
import cx from "classnames";
import { Tab, Tabs, TabsList } from "components/Tabs";
import { useEventCounts } from "hooks/firestore/v2/betting/useEventCounts";
import type { BettingRouteParams } from "./hooks/useBettingFilters";
import type { Hub } from "hooks/graphql/useHubs";
import { getTotalCountForSports } from "utilities/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "components/Button";
import { ReactComponent as SGMIcon } from "components/layout/assets/icon-sgm.svg";

import * as styles from "./BettingTabs.module.scss";

type BettingTabsProps = {
  hub: Hub;
  filters: BettingRouteParams;
  setFilters: (params: Partial<BettingRouteParams>) => void;
  listClassName?: string;
  canSGMBet?: boolean;
  setPage: (page: number) => void;
};

const SGMFilterButton = ({
  shouldHide,
  active,
  onClick,
}: { shouldHide: boolean; active: boolean; onClick: () => void }) => {
  return (
    <AnimatePresence>
      {!shouldHide && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
        >
          <Button
            className={cx(styles.sgmButton, {
              [styles.active]: active,
            })}
            onClick={onClick}
          >
            <SGMIcon />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const BettingTabs = ({
  hub,
  filters,
  setFilters,
  canSGMBet,
  setPage,
}: BettingTabsProps) => {
  const { eventCounts, isLoading: eventCountsLoading } = useEventCounts();

  const { outrightCount, matchCount } = useMemo(() => {
    const slugs = hub.titles.map(({ slug }) => slug);

    return {
      outrightCount: getTotalCountForSports(eventCounts, slugs, ["outrights"]),
      matchCount: getTotalCountForSports(eventCounts, slugs, ["matches"]),
    };
  }, [eventCounts, hub.titles]);

  const getTotalEventsForSport = (sport: string) => {
    return {
      totalOutrights: eventCounts?.outrights?.[sport] ?? 0,
      totalMatches: eventCounts?.matches?.[sport] ?? 0,
    };
  };

  const handleTabChange = (newTab: "next" | "outrights") => {
    // filter out the sports that have no events on the tab that we are
    // switching to
    const filteredSports =
      filters.sports?.filter((sport) => {
        const sportLower = sport.toLowerCase();
        const { totalOutrights, totalMatches } =
          getTotalEventsForSport(sportLower);

        return (
          (newTab === "outrights" && totalOutrights > 0) ||
          (newTab === "next" && totalMatches > 0)
        );
      }) ?? [];

    // if we are viewing a specific esport or sport it'll be in the route
    // params, for esports under `hub`, for sports it is under `sport`. We need
    // to make sure we push that value into the sports param if we have matches
    // or outrights available to view
    if ((filters.hub || filters.sport) && !filters.sports?.length) {
      const sport = hub.slug === "esports" ? filters.hub : filters.sport;
      const sportLower = sport?.toLowerCase();
      const { totalOutrights, totalMatches } = getTotalEventsForSport(
        sportLower as string,
      );

      if (
        (newTab === "outrights" && totalOutrights > 0) ||
        (newTab === "next" && totalMatches > 0)
      ) {
        filteredSports.push(sport as string);
      }
    }

    if (hub.slug === "esports") {
      setFilters({
        hub: undefined,
        sport: undefined,
        tab: newTab,
        page: 1,
        tour: [],
        sports: filteredSports,
      });
      setPage(1);
    } else {
      setFilters({
        tab: newTab,
        page: 1,
        tour: [],
        sgm: undefined,
        sport: undefined,
        sports: filteredSports,
      });
      setPage(1);
    }
  };

  const showSgmButton =
    filters.tab === "next" && hub.slug === "sports" && canSGMBet;

  return (
    <Tabs value={filters.tab} onValueChange={handleTabChange}>
      <TabsList
        containerClassName={styles.tabsListContainer}
        widget={
          <SGMFilterButton
            shouldHide={!showSgmButton}
            active={filters.sgm === "true"}
            onClick={() => {
              setFilters({ sgm: filters.sgm === "true" ? undefined : "true" });
            }}
          />
        }
      >
        <Tab
          value="next"
          badge={eventCountsLoading ? null : matchCount.toString()}
        >
          Next to Go
        </Tab>
        <Tab
          value="outrights"
          badge={eventCountsLoading ? null : outrightCount.toString()}
        >
          Outrights
        </Tab>
      </TabsList>
    </Tabs>
  );
};

BettingTabs.displayName = "BettingTabs";
